import React, { useState, useContext, useEffect } from "react";
import ChatContainer from "../components/ChatContainer";
import RecordingTaskFeedback from "../components/RecordingTaskFeedback";
import FileTree from "../components/FileTree";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { SessionContext } from "../contexts/SessionContext";
import { useRemoteFileActionsContext } from "../contexts/RemoteFileActionsContext";
import { useLocalFileActionsContext } from "../contexts/LocalFileActionsContext";
import EditorContainer from "../components/EditorContainer";
import ProjectSelector from "../components/ProjectSelector";
import TopBar from "../components/TopBar";
import BrowserScreenshotContainer from "../components/BrowserScreenshotContainer";
import styles from "../styles/CodeScreenStyles";
import ButtonGroup from "../components/ButtonGroup";
import GitChangesList from "../components/GitChangesList";
import GitLogs from "../components/GitLogs";
import DynamicTabSelector from "../components/DynamicTabSelector";
import ApiModeDropdown from "../components/ApiModeDropdown";
import QuickModeSwitch from "../components/QuickModeSwitch";
import { useIDEContext } from "../contexts/IDEContext";

const CodeScreen = () => {
  const { filesList, setFilesList, selectedRepoName, setSelectedRepoName } =
    useIDEContext();
  const [imageSrc, setImageSrc] = useState("");
  const [noScreenshot, setNoScreenshot] = useState(false);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState("");
  const [isBrowserVisible, setIsBrowserVisible] = useState(true);
  const [commitMessage, setCommitMessage] = useState("");
  const [gitChanges, setGitChanges] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const [selectedTab, setSelectedTab] = useState(0); // Track selected tab in mobile view

  const containerStyle = {
    ...styles.container,
    flexDirection: isMobile ? "column" : "row",
    height: "100vh",
  };

  const leftSectionStyle = {
    ...styles.leftSection,
    maxWidth: isMobile ? "100%" : "50%",
    flex: isMobile ? 3 : 1,
    marginBottom: isMobile ? "10px" : "0",
  };

  const rightSectionStyle = {
    ...styles.rightSection,
    maxWidth: isMobile ? "100%" : "50%",
  };

  const { handleToggleTracking } = useContext(SessionContext);
  const { projects, activeProject, handleProjectChange, lastRecordedTask } =
    useContext(FirebaseContext);
  const {
    runApp,
    fetchFilesFromRepo,
    downloadRepo,
    getFile,
    updateAddFiles,
    commitChanges,
    pushChanges,
    pullChanges,
    getGitChanges,
    revertGitState,
    rollbackRepo,
  } = useRemoteFileActionsContext();
  const { tabs, addTab, resetTabState } = useLocalFileActionsContext();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeProject && activeProject.gitRepo) {
      setSelectedRepoUrl(activeProject.gitRepo);
      setSelectedRepoName(activeProject.gitRepo.split("/").pop());
    }
  }, [activeProject, setSelectedRepoName]);

  useEffect(() => {
    if (selectedRepoName) {
      handleFetchingFiles();
    }
  }, [selectedRepoName]);

  const handleFileSelection = async (fileName) => {
    const fileData = await getFile(fileName);
    addTab(fileName, fileData);
    handleMobileFileSelect();
  };

  const handleUpdateFilesClick = async () => {
    const filesToUpdate = tabs
      .filter((tab) => tab.state === "new" || tab.state === "update")
      .map((tab) => ({
        filePath: tab.fileName,
        newContent: tab.fileContent,
      }));
    if (filesToUpdate.length === 0) {
      console.log("No files to update.");
      return;
    }
    console.log("Files to update:", filesToUpdate);
    await updateAddFiles(filesToUpdate);
    const changes = await getGitChanges(selectedRepoName);
    setGitChanges(changes);
    await handleFetchingFiles();
    tabs.forEach((tab) => {
      resetTabState(tab.fileName);
    });
  };

  const handleRunClick = async () => {
    if (!selectedRepoName) return;
    const screenshotData = await runApp(selectedRepoName);
    // TODO show screenshot in browser later
    // setImageSrc(screenshotData);
    // setNoScreenshot(!screenshotData);
    // setIsBrowserVisible(true);
  };

  const handleFetchingFiles = async () => {
    if (!selectedRepoName) return;
    const files = await fetchFilesFromRepo(selectedRepoName);
    setFilesList(files);
  };

  const handleDownloadRepoClick = async () => {
    if (!selectedRepoUrl) return;
    const gitUrl = `${selectedRepoUrl}.git`;
    await downloadRepo(gitUrl);
  };

  const handleCommitClick = async () => {
    if (!selectedRepoName || !commitMessage) return;
    await commitChanges(selectedRepoName, commitMessage);
    setCommitMessage("");
  };

  const handlePushClick = async () => {
    if (!selectedRepoName) return;
    await pushChanges(selectedRepoName);
  };

  const handlePullClick = async () => {
    if (!selectedRepoName) return;
    await pullChanges(selectedRepoName);
    await handleFetchingFiles();
  };

  const handleGetGitChangesClick = async () => {
    if (!selectedRepoName) return;
    const changes = await getGitChanges(selectedRepoName);
    setGitChanges(changes);
  };

  const handleRevertFile = async (file) => {
    if (!selectedRepoName) return;
    await revertGitState(selectedRepoName, file);
    const updatedChanges = await getGitChanges(selectedRepoName);
    setGitChanges(updatedChanges);
  };

  const handleCloseBrowser = () => {
    setIsBrowserVisible(false);
  };

  const handleShowDiff = (originalContent, currentContent) => {
    console.log("Original Content:", originalContent);
    console.log("Current Content:", currentContent);
  };

  const handleRollbackRepoClick = async () => {
    if (!selectedRepoName) return;
    await rollbackRepo(selectedRepoName);
    const updatedChanges = await getGitChanges(selectedRepoName);
    setGitChanges(updatedChanges);
  };

  const handleMobileFileSelect = () => {
    if (isMobile) setSelectedTab(1);
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        paddingTop: isMobile ? "100px" : "50px",
      }}
    >
      <TopBar
        centerContainer={
          <ProjectSelector
            projects={projects}
            activeProject={activeProject}
            onProjectChange={handleProjectChange}
          />
        }
        rightContainer={
          <ButtonGroup
            selectedRepoName={selectedRepoName}
            selectedRepoUrl={selectedRepoUrl}
            tabs={tabs}
            commitMessage={commitMessage}
            setCommitMessage={setCommitMessage}
            handleRunClick={handleRunClick}
            handleDownloadRepoClick={handleDownloadRepoClick}
            handleUpdateFilesClick={handleUpdateFilesClick}
            handleCommitClick={handleCommitClick}
            handlePushClick={handlePushClick}
            handlePullClick={handlePullClick}
            handleGetGitChangesClick={handleGetGitChangesClick}
            handleRollbackRepoClick={handleRollbackRepoClick}
          />
        }
      />
      <QuickModeSwitch />
      <ApiModeDropdown />
      {lastRecordedTask && (
        <RecordingTaskFeedback
          task={lastRecordedTask}
          onPlay={() => handleToggleTracking(lastRecordedTask)}
          onStop={() => handleToggleTracking(lastRecordedTask)}
        />
      )}
      {gitChanges.length > 0 && (
        <GitChangesList
          changes={gitChanges}
          onRevert={handleRevertFile}
          onShowDiff={handleShowDiff}
        />
      )}
      <GitLogs />
      {isMobile ? (
        <>
          <DynamicTabSelector
            tabs={["Files", "Editor", "Chat"]}
            selectedTabIndex={selectedTab}
            onTabSelected={setSelectedTab}
          />
          <div style={containerStyle}>
            {selectedTab === 0 && (
              <FileTree files={filesList} onSelectFile={handleFileSelection} />
            )}
            {selectedTab === 1 && <EditorContainer />}
            {selectedTab === 2 && <ChatContainer />}
          </div>
        </>
      ) : (
        <div style={containerStyle}>
          <div style={leftSectionStyle}>
            <div style={styles.verticalScrollContainer}>
              <div style={styles.chatContainer}>
                <ChatContainer />
              </div>
              <div style={styles.fileTreeContainer}>
                <FileTree
                  files={filesList}
                  onSelectFile={handleFileSelection}
                />
              </div>
            </div>
          </div>
          <div style={rightSectionStyle}>
            <EditorContainer />
          </div>
        </div>
      )}

      {isBrowserVisible && (imageSrc || noScreenshot) && (
        <BrowserScreenshotContainer
          imageSrc={imageSrc}
          noScreenshot={noScreenshot}
          onClose={handleCloseBrowser}
        />
      )}
    </div>
  );
};

export default CodeScreen;
